/**
 * Turns the backend's rendered Terms & Conditions into blocks the widget can lay out.
 *
 * The terms arrive as one flat run of HTML (see backend `campaigns.tags.render_terms_content`):
 * blocks separated by a double line break, each "bullet" a text line starting with "- ", and the
 * scoring and prize lists as blocks of single-line-break lines ("1: €100 Cash,"). Nothing marks
 * which block is which, so everything here keys off STRUCTURE and the campaign's own data, never
 * off wording — the same code has to work in every language.
 */

export type TermsList = {
  kind: "list";
  /** Each line's HTML, bullet prefix removed. */
  lines: string[];
};

export type TermsBlock = { kind: "text"; html: string } | TermsList;

/** Markup an operator authored with real structure: lay it out as-is instead of re-parsing. */
const AUTHORED_STRUCTURE = /<(?:ul|ol|li|table|h[1-6])[\s>/]/i;

const LINE_BREAK = /<br\s*\/?>/i;
const BLOCK_BREAK = /(?:\s*<br\s*\/?>\s*){2,}/i;
const BULLET_PREFIX = /^(?:[-–•*]|&ndash;|&bull;)\s+/;
const TAG = /<[^>]+>/g;

/** `null` when the terms carry their own structure (lists, tables, headings). */
export const parseTermsBlocks = (html: string): TermsBlock[] | null => {
  if (AUTHORED_STRUCTURE.test(html)) return null;
  const flat = html
    .trim()
    // Paragraph boundaries are block boundaries too.
    .replace(/<\/p>\s*<p[^>]*>/gi, "<br><br>")
    .replace(/^<p[^>]*>/i, "")
    .replace(/<\/p>\s*$/i, "");
  const blocks: TermsBlock[] = [];
  // Bullets the template glued together ("…all players.- Any spins…"): a non-space character
  // straight into "- " and a capital letter is a new bullet. Ordinary hyphenated words never have
  // a space after the hyphen, so they are left alone — and neither is a "- " right after a tag,
  // which is a list line ("…<br/>- Play 50 rounds"), not a glued bullet.
  const unglued = flat.replace(/([^\s>])- (?=\p{Lu})/gu, "$1<br><br>- ");
  for (const raw of unglued.split(BLOCK_BREAK)) {
    const lines = raw
      .split(LINE_BREAK)
      .map((line) => line.trim().replace(BULLET_PREFIX, ""))
      .filter((line) => plainText(line).length > 0);
    if (lines.length === 0) continue;
    blocks.push(
      lines.length > 1
        ? { kind: "list", lines }
        : { kind: "text", html: lines[0] }
    );
  }
  return blocks;
};

/** A line's visible text, for matching: tags stripped, entities decoded, trailing , ; . dropped. */
export const plainText = (html: string): string =>
  html
    .replace(TAG, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[,;.]+$/, "")
    .trim();

/**
 * Whether most of a list's lines name one of `names` (prize titles, task titles). Matching the
 * campaign's own data is what lets the widget swap a text list for its real component in any
 * language. "Most" (≥ 60%), not "all": operators sometimes add a line of their own.
 */
export const listMatches = (list: TermsList, names: string[]): boolean => {
  const wanted = names.map((n) => plainText(n).toLowerCase()).filter(Boolean);
  if (wanted.length === 0) return false;
  const hits = list.lines.filter((line) => {
    const text = plainText(line).toLowerCase();
    return wanted.some((name) => text.includes(name));
  }).length;
  return hits >= Math.ceil(list.lines.length * 0.6);
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export type TermsSegment = {
  kind: "text" | "bet" | "date";
  value: string;
};

/**
 * The campaign's own start or end as the backend's terms template writes it ("02 October 2026,
 * 09:48 UTC", in UTC for the widget, with the month word translated): matched on the parts that do
 * not depend on language — the day, the year and the time of THIS date — with any month word in
 * between. So only the value Unibo filled in is picked out; a date the operator typed stays plain
 * (unless it is literally the same moment).
 */
const datePattern = (iso: string): string | null => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = d.getUTCDate();
  const time = `${pad(d.getUTCHours())}[:.]${pad(d.getUTCMinutes())}`;
  return `0?${day}\\.?\\s+[^\\s\\d<>,.]+\\.?\\s+${d.getUTCFullYear()},?\\s+${time}(?:\\s+[A-Z]{2,5})?`;
};

/**
 * A plain-text sentence cut into the runs the widget renders as its own badges — ONLY values
 * Unibo's template filled in from the campaign, recognised by being the campaign's actual values:
 * - the minimum bet, by its exact display string ("€0.20");
 * - the campaign's start and end dates (see `datePattern`).
 * Anything else, however much it looks like a date or an amount, is the operator's text and stays
 * plain.
 */
export const termsSegments = (
  text: string,
  { minimumBet, dates = [] }: { minimumBet?: string | null; dates?: string[] }
): TermsSegment[] => {
  const datePatterns = dates
    .map(datePattern)
    .filter((p): p is string => p != null);
  const betPattern = minimumBet?.trim()
    ? `${escapeRegExp(minimumBet.trim())}(?![\\d.,]?\\d)`
    : null;
  if (!datePatterns.length && !betPattern)
    return [{ kind: "text", value: text }];
  const parts = [
    datePatterns.length ? `(${datePatterns.join("|")})` : "(?!)",
    betPattern ? `(${betPattern})` : "(?!)",
  ];
  const all = new RegExp(parts.join("|"), "gu");
  const out: TermsSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(all)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: "text", value: text.slice(last, at) });
    out.push({ kind: m[1] ? "date" : "bet", value: m[0] });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", value: text.slice(last) });
  return out;
};
